/**
 * journey.js — iter880 · reports each step someone takes to get into
 * FLYT (sign in with Google / Apple / email, create account, verify,
 * pick a persona, app opens) to the App Health dashboard (Admin → 7 AM
 * App Health) and mirrors it to PostHog.
 *
 * Every attempt is `start` → `ok` | `fail` | `cancel`. The backend
 * scores success rate per step, per platform and per iOS app version —
 * so a release that breaks a step shows up red the same morning, the
 * way 1.4.13's Google sign-in should have.
 *
 * Fire-and-forget: never throws, never blocks the UI. Uses
 * `fetch(keepalive)` so a `start` posted right before a full-page
 * redirect (web Google sign-in) still lands. No emails, names or
 * tokens are ever sent — only step, outcome, platform, app version and
 * a short error message.
 */
import { Capacitor } from '@capacitor/core';
import { track } from './analytics';

const JOURNEY_URL = `${process.env.REACT_APP_BACKEND_URL}/api/track/journey`;

let _infoPromise = null;
const appInfo = () => {
  if (!_infoPromise) {
    _infoPromise = (async () => {
      try {
        if (!Capacitor.isNativePlatform()) return {};
        const { App } = await import('@capacitor/app');
        const i = await App.getInfo();
        return { version: i?.version, build: i?.build };
      } catch { return {}; }
    })();
  }
  return _infoPromise;
};

const platform = () => {
  try { return Capacitor.getPlatform() || 'web'; } catch { return 'web'; }
};

export const newAttemptId = () => {
  try {
    const a = new Uint8Array(12);
    window.crypto.getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  }
};

// Outcomes that are the person's own doing (cancelled the sheet, wrong
// password, account already exists, too many tries) — reported as
// `cancel` so they never count against a step's health. Network errors,
// 5xx and anything unexpected count as real failures.
const USER_SIDE_HTTP = new Set([401, 403, 409, 429]);
const isUserSide = (err, extra) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status && (USER_SIDE_HTTP.has(status) || (extra && extra.includes(status)))) return true;
  const s = `${err?.code || ''} ${err?.error || ''} ${err?.message || err || ''}`.toLowerCase();
  return /cancel|popup_closed|closed by user|user_cancelled|1001|aborted by user/.test(s);
};

const errorText = (err) => {
  if (!err) return null;
  const d = err?.response?.data?.detail;
  const base = typeof d === 'string' ? d : (d?.message || err?.message || String(err));
  const status = err?.response?.status;
  return `${status ? `HTTP ${status}: ` : ''}${base}`.slice(0, 300);
};

/** Low-level: report one event. `opts.platform` overrides the detected
 *  platform (AuthCallback in Safari reports for the iOS attempt). */
export function journey(step, status, opts = {}) {
  try {
    appInfo().then((info) => {
      const body = {
        step,
        status,
        attempt: opts.attempt || newAttemptId(),
        platform: opts.platform || platform(),
        version: opts.platform && opts.platform !== platform() ? null : (info.version || null),
        build: opts.platform && opts.platform !== platform() ? null : (info.build || null),
        error: opts.error ? String(opts.error).slice(0, 300) : null,
      };
      try {
        fetch(JOURNEY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
          credentials: 'omit',
        }).catch(() => {});
      } catch { /* ignore */ }
      try { track(`journey_${step}_${status}`, { platform: body.platform, app_version: body.version }); } catch { /* ignore */ }
    });
  } catch { /* never break the caller */ }
}

/**
 * Start an attempt. Returns handles to finish it exactly once:
 *   const j = journeyAttempt('login_email');
 *   try { …; j.ok(); } catch (e) { j.fail(e); throw e; }
 * `fail(err)` automatically downgrades user-side outcomes to `cancel`.
 */
export function journeyAttempt(step, attemptId, opts = {}) {
  // `opts.userSideStatuses` — extra HTTP codes that mean "the person
  // typed something invalid" for this step (e.g. 400 on sign-up).
  const extra = opts.userSideStatuses || null;
  const attempt = attemptId || newAttemptId();
  let done = false;
  journey(step, 'start', { attempt });
  const finish = (status, error) => {
    if (done) return;
    done = true;
    journey(step, status, { attempt, error });
  };
  return {
    id: attempt,
    ok: () => finish('ok'),
    cancel: () => finish('cancel'),
    fail: (err) => (isUserSide(err, extra) ? finish('cancel', errorText(err)) : finish('fail', errorText(err))),
  };
}

/** Finish an attempt that was started on an earlier page (web Google
 *  redirect) — the id travels in sessionStorage under `key`. */
export function journeyResume(step, key) {
  let attempt = null;
  try { attempt = window.sessionStorage.getItem(key); window.sessionStorage.removeItem(key); } catch { /* ignore */ }
  if (!attempt) return null;
  let done = false;
  const finish = (status, error) => { if (!done) { done = true; journey(step, status, { attempt, error }); } };
  return {
    id: attempt,
    ok: () => finish('ok'),
    fail: (err) => (isUserSide(err) ? finish('cancel', errorText(err)) : finish('fail', errorText(err))),
  };
}

/** Once per native launch — lets the dashboard spot a release where
 *  the app never even opens (crash on launch). */
let _openSent = false;
export function journeyAppOpen() {
  if (_openSent) return;
  try { if (!Capacitor.isNativePlatform()) return; } catch { return; }
  _openSent = true;
  journey('app_open', 'ok');
}
